import React from 'react';
import './PhoneInput.scss';
import countriesData from './countries.json';
import { FormInput } from 'components/FormInput/FormInput';

const COUNTRIES = (countriesData || [])
  .map((c) => ({
    iso2: c.code,
    name: c.name,
    flag: c.flag,
    dialCode: String(c.dial_code || '').replace(/\D/g, ''),
    nationalNumberLengths: Array.isArray(c.national_number_lengths)
      ? c.national_number_lengths
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0)
      : null,
  }))
  .filter((c) => c.iso2 && c.dialCode)
  .sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_BY_ISO2 = new Map(COUNTRIES.map((c) => [c.iso2, c]));

const COUNTRIES_BY_DIALCODE_DESC = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.iso2 === 'US') ||
  COUNTRIES[0] || {
    iso2: 'US',
    name: 'United States',
    flag: '🇺🇸',
    dialCode: '1',
  };

const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');

const findCountryFromE164 = (e164) => {
  const digits = normalizeDigits(e164);

  for (const c of COUNTRIES_BY_DIALCODE_DESC) {
    if (digits.startsWith(c.dialCode)) {
      return c;
    }
  }

  return null;
};

const isValidNationalLength = (country, nationalDigits) => {
  const nn = normalizeDigits(nationalDigits);
  if (!nn) return false;

  const lengths = country?.nationalNumberLengths;
  if (!lengths || lengths.length === 0) {
    return nn.length === 10;
  }

  return lengths.includes(nn.length);
};

export const validatePhoneE164 = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return true;

  const digits = normalizeDigits(raw);
  if (!digits) return 'Phone number is required';

  if (!raw.startsWith('+')) {
    return 'Phone number must include country code';
  }

  if (digits.length < 8 || digits.length > 15) {
    return 'Invalid phone number';
  }

  const country = findCountryFromE164(raw) || DEFAULT_COUNTRY;
  const national = digits.startsWith(country.dialCode)
    ? digits.slice(country.dialCode.length)
    : digits;

  if (!isValidNationalLength(country, national)) {
    return `Invalid phone number for ${country.name}`;
  }

  return true;
};

export const PhoneInput = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  placeholder,
  focusVariant = 'primary-gradient',
}) => {
  const [countryIso2, setCountryIso2] = React.useState(DEFAULT_COUNTRY.iso2);
  const [nationalNumber, setNationalNumber] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const rootRef = React.useRef(null);
  const pickerButtonRef = React.useRef(null);
  const listboxId = React.useId();

  const selectedCountry = React.useMemo(() => {
    return COUNTRY_BY_ISO2.get(countryIso2) || DEFAULT_COUNTRY;
  }, [countryIso2]);

  const filteredCountries = React.useMemo(() => {
    const q = String(query || '')
      .trim()
      .toLowerCase();
    if (!q) return COUNTRIES;

    const qDigits = q.replace(/\D/g, '');
    return COUNTRIES.filter((c) => {
      const name = String(c.name || '').toLowerCase();
      const iso2 = String(c.iso2 || '').toLowerCase();
      const dial = String(c.dialCode || '');
      if (name.includes(q)) return true;
      if (iso2.includes(q)) return true;
      if (qDigits && dial.startsWith(qDigits)) return true;
      if (q.startsWith('+') && qDigits && dial.startsWith(qDigits)) return true;
      return false;
    });
  }, [query]);

  React.useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const onDocKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        setQuery('');
        pickerButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);

    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen]);

  React.useEffect(() => {
    const raw = String(value || '').trim();
    if (!raw) {
      setNationalNumber('');
      return;
    }

    const detected = findCountryFromE164(raw);
    const digits = normalizeDigits(raw);

    if (raw.startsWith('+') && detected) {
      setCountryIso2((prev) => (prev === detected.iso2 ? prev : detected.iso2));
      setNationalNumber(digits.slice(detected.dialCode.length));
      return;
    }

    setNationalNumber(digits);
  }, [value]);

  const emit = React.useCallback(
    (iso2, national) => {
      const country = COUNTRY_BY_ISO2.get(iso2) || DEFAULT_COUNTRY;
      const nn = normalizeDigits(national);
      const e164 = nn ? `+${country.dialCode}${nn}` : '';
      onChange?.(e164);
    },
    [onChange]
  );

  const handleSelectCountry = (nextIso2) => {
    setCountryIso2(nextIso2);
    emit(nextIso2, nationalNumber);
    setIsOpen(false);
    setQuery('');
    pickerButtonRef.current?.focus();
  };

  const handleNationalChange = (nextValue) => {
    const next = normalizeDigits(nextValue);
    setNationalNumber(next);
    emit(countryIso2, next);
  };

  return (
    <div
      ref={rootRef}
      className={`phone-input ${error ? 'phone-input--error' : ''}`}
    >
      <div className='phone-input__control'>
        <div className='phone-input__picker'>
          <button
            ref={pickerButtonRef}
            type='button'
            className='phone-input__picker-button'
            onClick={() => setIsOpen((v) => !v)}
            disabled={disabled}
            aria-label='Select country'
            aria-haspopup='listbox'
            aria-expanded={isOpen}
            aria-controls={listboxId}
          >
            <span className='phone-input__picker-flag'>
              {selectedCountry.flag}
            </span>
            <span className='phone-input__picker-code'>
              +{selectedCountry.dialCode}
            </span>
            <span className='phone-input__picker-caret'>▾</span>
          </button>

          {isOpen && (
            <div
              id={listboxId}
              className='phone-input__dropdown'
              role='listbox'
              aria-label='Country list'
            >
              <input
                className='phone-input__search'
                type='text'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Search country or code'
                autoFocus
                aria-label='Search country'
              />

              <div className='phone-input__list'>
                {filteredCountries.length === 0 ? (
                  <div className='phone-input__empty'>No search found</div>
                ) : (
                  filteredCountries.map((c) => (
                    <button
                      key={`${c.iso2}-${c.dialCode}`}
                      type='button'
                      className='phone-input__item'
                      onClick={() => handleSelectCountry(c.iso2)}
                      role='option'
                      aria-selected={c.iso2 === countryIso2}
                    >
                      <span className='phone-input__item-flag'>{c.flag}</span>
                      <span className='phone-input__item-name'>{c.name}</span>
                      <span className='phone-input__item-code'>
                        +{c.dialCode}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <FormInput
          id={id}
          label=''
          type='tel'
          value={nationalNumber}
          onChange={handleNationalChange}
          onBlur={onBlur}
          placeholder={placeholder || 'Phone number'}
          disabled={disabled}
          error={error}
          showErrorMsg={false}
          focusVariant={focusVariant}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'tel',
          }}
        />
      </div>
    </div>
  );
};
